import { useEffect, useRef } from 'react';

import { PopupBoxProps, PopupBoxButtonsProps } from '../interfaces';

import './PopupBox.css';

import close from '../svg/Menue-schliessen.svg';
import info from '../svg/Tooltip-Info.svg';

function PopupBox({ boxKind, boxTitle, boxText, onConfirm, onReject, onCancel }:PopupBoxProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const confirmButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    confirmButtonRef.current?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCancel();
      }

      if (e.key === 'Tab' && dialogRef.current) {
        const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onCancel]);

  return (
    <div
      ref={dialogRef} 
      role="dialog" 
      aria-labelledby="dialog-label" 
      aria-modal="true" 
      className="popupbox"
    >
      <div className="popupbox-box">
        <div className="popupbox-title">
          <h2 id="dialog-label">{boxTitle}</h2>
          <button 
            aria-label="Schließen"
            className="close-button" 
            onClick={onCancel}
          >
            <img aria-hidden="true" src={close} alt="" />
          </button>
        </div>
        <div className="popupbox-text">
          <img aria-hidden="true" src={info} alt="" />
          <p>{boxText}</p>
        </div>
        <div className="popupbox-buttons">
          <button
            ref={confirmButtonRef}
            type="button" 
            onClick={onConfirm}
          >
            {boxKind === 3 || boxKind === 4 ? 'Ja' : 'OK'}
          </button> 
          {boxKind !== 1 && (
            <button 
              type="button" 
              onClick={onReject}
            >
              {boxKind === 2 ? 'Abbrechen' : 'Nein'}
            </button>
          )}
          {boxKind === 4 && (
            <button 
              type="button" 
              onClick={onCancel}
            >
              Abbrechen
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default PopupBox;

function PopupBoxButtons({ boxKind, onConfirm, onCancel }:PopupBoxButtonsProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const secondButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (buttonRef.current) {
      buttonRef.current.focus();
    }
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onCancel]);

  function handleButtonKeyDown(event:React.KeyboardEvent<HTMLButtonElement >) {
    if (event.key === 'Tab' && event.shiftKey) {
      event.preventDefault();
      secondButtonRef.current?.focus();
    }
  }

  function handleSecondButtonKeyDown(event:React.KeyboardEvent<HTMLButtonElement >) {
    if (event.key === 'Tab') {
      event.preventDefault();
      buttonRef.current?.focus();
    }
  }

  if (boxKind === 1) {
    return (
      <button
        ref={buttonRef} 
        type="button" 
        onClick={onConfirm}
        onKeyDown={handleButtonKeyDown}
      >
        OK
      </button>
    );
  } 
  
  if (boxKind === 2) {
    return (
      <>
        <button 
          ref={buttonRef} 
          type="button" 
          onClick={onConfirm}
          onKeyDown={handleButtonKeyDown}
        >
          OK
        </button>
        <button 
          ref={secondButtonRef} 
          type="button" 
          onClick={onCancel}
          onKeyDown={handleSecondButtonKeyDown}
        >
          Abbrechen
        </button>
      </>
    );
  } 
    
  return (
    <>
      <button 
        ref={buttonRef} 
        type="button" 
        onClick={onConfirm}
        onKeyDown={handleButtonKeyDown}
      >
        Ja
      </button>
      <button 
        ref={secondButtonRef} 
        type="button" 
        onClick={onCancel}
        onKeyDown={handleSecondButtonKeyDown}
      >
        Nein
      </button>
    </>
  );
}
